import { CalendarDateTime, now, parseDateTime } from "@internationalized/date";
import { format } from "date-fns";

export const APRIL = 4;
export const INCEPTION_START_YEAR = 2006;

export const FORMAT = {
    DOT_SEPARATED: "dd.MM.yy",
    DASH_SEPARATED: "dd-MM-yyyy",
    SLASH_SEPARATED: "dd/MM/yyyy",
    UTC_DATE_ONLY: "yyyy-MM-dd"
};

export const CURRENT_TAX = {
    id: "CURRENT_TAX_YEAR",
    display: "Current tax year",
    startDate: getStartOfTaxYearFor(nowDateTime()),
    endDate: nowDateTime()
};

export const LAST_TAX = {
    id: "LAST_TAX_YEAR",
    display: "Last tax year",
    startDate: getStartOfTaxYearFor(nowDateTime().subtract({ years: 1 })),
    endDate: getEndOfTaxYearFor(nowDateTime().subtract({ years: 1 }))
};

export const THREE_MONTHS = {
    id: "LAST_3_MONTHS",
    display: "Last 3 months",
    startDate: nowDateTime().subtract({ months: 3 }),
    endDate: nowDateTime()
};

export const SIX_MONTHS = {
    id: "LAST_6_MONTHS",
    display: "Last 6 months",
    startDate: nowDateTime().subtract({ months: 6 }),
    endDate: nowDateTime()
};

export const TWELVE_MONTHS = {
    id: "LAST_12_MONTHS",
    display: "Last 12 months",
    startDate: nowDateTime().subtract({ months: 12 }),
    endDate: nowDateTime()
};

export const THREE_YEARS = {
    id: "LAST_3_YEARS",
    display: "Last 3 years",
    startDate: nowDateTime().subtract({ years: 3 }),
    endDate: nowDateTime()
};

export const FIVE_YEARS = {
    id: "LAST_5_YEARS",
    display: "Last 5 years",
    startDate: nowDateTime().subtract({ years: 5 }),
    endDate: nowDateTime()
};

export const ALL_DATES = {
    id: "ALL_DATES",
    display: "All dates",
    startDate: new CalendarDateTime(INCEPTION_START_YEAR, 1, 1),
    endDate: nowDateTime()
};

export function getStartOfTaxYearFor(date: CalendarDateTime) {
    const year = getTaxYear(date);
    return new CalendarDateTime(year, APRIL, 6).set({
        hour: 0,
        minute: 0,
        second: 0
    });
}

export function getEndOfTaxYearFor(date: CalendarDateTime) {
    const year = getTaxYear(date);
    return new CalendarDateTime(year + 1, APRIL, 5).set({
        hour: 23,
        minute: 59,
        second: 59
    });
}

export function getTaxYear(date: CalendarDateTime) {
    return isBeforeSixthApril(date) ? date.year - 1 : date.year;
}

export function isDateBetween(
    dateToCheck: CalendarDateTime | string,
    startDate: CalendarDateTime | string,
    endDate: CalendarDateTime | string,
    inclusive = false
) {
    if (typeof dateToCheck === "string") {
        dateToCheck = parseDateTime(dateToCheck);
    }
    if (typeof startDate === "string") {
        startDate = parseDateTime(startDate);
    }
    if (typeof endDate === "string") {
        endDate = parseDateTime(endDate);
    }
    return inclusive
        ? dateToCheck.compare(startDate) >= 0 && dateToCheck.compare(endDate) <= 0
        : dateToCheck.compare(startDate) > 0 && dateToCheck.compare(endDate) < 0;
}

export function isBeforeSixthApril(date: CalendarDateTime) {
    const beforeApril = date.month < APRIL;
    const inApril = date.month === APRIL;
    const before6thOfMonth = date.day < 6;
    return beforeApril || (inApril && before6thOfMonth);
}

export function formatDate(
    dateToFormat: string | CalendarDateTime,
    formatString = FORMAT.SLASH_SEPARATED
) {
    if (!dateToFormat) {
        return "";
    }
    let date;
    if (dateToFormat instanceof CalendarDateTime) {
        date = dateToFormat.toDate("Europe/London");
    } else {
        date = new Date(dateToFormat);
    }
    if (isNaN(date.getTime())) {
        return "";
    }
    return format(date, formatString);
}

export function isBetween(
    date: CalendarDateTime,
    range: { startDate: CalendarDateTime; endDate: CalendarDateTime }
) {
    return date.compare(range.startDate) >= 0 && date.compare(range.endDate) <= 0;
}

export function getCurrentTaxYear() {
    return getTaxYear(nowDateTime());
}

export function nowDateTime() {
    return new CalendarDateTime(
        now("Europe/London").year,
        now("Europe/London").month,
        now("Europe/London").day,
        now("Europe/London").hour,
        now("Europe/London").minute,
        now("Europe/London").second
    );
}
